import { v4 as uuidv4 } from "uuid";
import { Task, TaskStatus, TaskType } from "../types/taskTypes";

/** Aligné avec TasksContext (un seul contrat d’événements window). */
export const LEGAL_TASK_ADDED_EVENT = "infravia-add-legal-task" as const;
export const LEGAL_TASK_PROGRESS_EVENT = "infravia-legal-task-progress" as const;

/** Demande un GET /legal/legal_jobs immédiat (après annulation, sans attendre le poll 60s). */
export const LEGAL_JOBS_SYNC_NOW_EVENT = "infravia-legal-jobs-sync-now" as const;

/**
 * Session navigateur : id de la tâche Legal pour laquelle un flux SSE est attendu dans CET onglet.
 * Vide après fermeture d’onglet → permet de détecter réouverture / autre onglet et de réconcilier la barre.
 */
export const LEGAL_ACTIVE_SSE_TASK_SESSION_KEY =
  "infravia-legal-active-sse-task-id";

/** Dernière requête legal avec task_id : pour finaliser la barre après F5 (ref React perdue). */
export const LEGAL_PENDING_TASK_SESSION_KEY =
  "infravia-pending-legal-task-id";

/** Thread correspondant à la dernière tâche exhaustive en cours (sert à afficher "thinking" avant sync ES). */
export const LEGAL_PENDING_TASK_THREAD_SESSION_KEY =
  "infravia-pending-legal-task-thread-id";

export function setPendingLegalTaskId(taskId: string): void {
  try {
    sessionStorage.setItem(LEGAL_PENDING_TASK_SESSION_KEY, taskId);
  } catch {
    /* ignore */
  }
}

export function setPendingLegalTaskThreadId(threadId: string): void {
  try {
    sessionStorage.setItem(LEGAL_PENDING_TASK_THREAD_SESSION_KEY, threadId);
  } catch {
    /* ignore */
  }
}

export function clearPendingLegalTaskSession(): void {
  try {
    sessionStorage.removeItem(LEGAL_PENDING_TASK_SESSION_KEY);
    sessionStorage.removeItem(LEGAL_PENDING_TASK_THREAD_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function setLegalActiveSseTaskId(taskId: string): void {
  try {
    sessionStorage.setItem(LEGAL_ACTIVE_SSE_TASK_SESSION_KEY, taskId);
  } catch {
    /* quota / mode privé */
  }
}

export function clearLegalActiveSseTaskId(): void {
  try {
    sessionStorage.removeItem(LEGAL_ACTIVE_SSE_TASK_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Crée une tâche UI « recherche legal » + id stable pour le payload API (task_id après snake_case).
 */
export function createLegalSearchTaskFromQuery(
  trimmedQuery: string,
  pathCount: number,
  threadId?: string,
  options?: { requiresServerResume?: boolean }
): { taskId: string; task: Task } {
  const taskId = uuidv4();
  const filename =
    trimmedQuery.slice(0, 80) + (trimmedQuery.length > 80 ? "…" : "");
  const requiresServerResume = options?.requiresServerResume === true;
  return {
    taskId,
    task: {
      id: taskId,
      type: TaskType.LEGAL_SEARCH,
      timestamp: Date.now(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      ...(threadId ? { threadId } : {}),
      ...(requiresServerResume ? { requiresServerResume: true } : {}),
      file: {
        filename,
        nbPages: Math.max(pathCount, 1),
        nbProcessed: 0,
        nbProcessedSummary: 0,
        status: "processing",
      },
      status: TaskStatus.PROCESSING,
    },
  };
}

export function emitLegalTaskAdded(task: Task): void {
  window.dispatchEvent(
    new CustomEvent(LEGAL_TASK_ADDED_EVENT, { detail: task })
  );
}

export function emitLegalTaskProgressCompleted(
  taskId: string,
  processedDocs: number,
  totalDocs: number
): void {
  clearPendingLegalTaskSession();
  window.dispatchEvent(
    new CustomEvent(LEGAL_TASK_PROGRESS_EVENT, {
      detail: {
        taskId,
        task_id: taskId,
        status: "completed",
        processedDocs,
        totalDocs,
      },
    })
  );
}
